import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllCampaignsForSearch.graphql';
import { Query, QueryallCampaignsForSearchArgs } from '../../__generated__/globalTypes';

export const useAllCampaignsForSearchQuery = (options?: QueryHookOptions<Query, QueryallCampaignsForSearchArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallCampaignsForSearchArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllCampaignsForSearchLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallCampaignsForSearchArgs>
): QueryResult<Query, QueryallCampaignsForSearchArgs> & {
  getAllCampaignsForSearch: LazyQueryExecFunction<Query, QueryallCampaignsForSearchArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllCampaignsForSearch, result] = useLazyQuery<Query, QueryallCampaignsForSearchArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllCampaignsForSearch, ...result };
};

export const useAllCampaignsForSearchPromiseQuery = () => {
  const client = useApolloClient();

  const getAllCampaignsForSearch = useCallback(
    (options?: Omit<QueryOptions<QueryallCampaignsForSearchArgs, Query>, 'query'>) =>
      client.query<Query, QueryallCampaignsForSearchArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllCampaignsForSearch };
};
// Suspense Query Hooks
export const useAllCampaignsForSearchSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallCampaignsForSearchArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallCampaignsForSearchArgs>(QUERY, {
    ...options
  });

  return result;
};
